"use client";

import React, { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { Play, MousePointerClick, Terminal, CircleCheckBig } from "lucide-react";
import { useFastForward } from "@/contexts/FastForwardContext";
import { getViewModeChrome } from "../view-mode-chrome";

interface AwaitUserExecutionButtonProps {
  sessionId?: string;
  toolCallId?: string;
  args?: any;
  viewMode?: string;
}

export function AwaitUserExecutionButton({ sessionId, toolCallId, args, viewMode}: AwaitUserExecutionButtonProps) {
  const chrome = getViewModeChrome(viewMode);
  const { 
    setDemoActionData, 
    setToolCallAction,
    resetStateForNewTool,
    markToolCallExecuted,
    isToolCallExecuted,
    executedToolCallIds
  } = useFastForward();
  const [countdown, setCountdown] = useState(60);
  const [isExecuted, setIsExecuted] = useState(false);
  
  // Set demo action data and action type when component mounts
  useEffect(() => {
    if (sessionId && toolCallId) {
      setDemoActionData(sessionId, toolCallId);
      setToolCallAction(toolCallId, "await_user_execution");
      // Reset global state for new tool call (but don't affect individual tool displays)
      resetStateForNewTool();
    }
  }, [sessionId, toolCallId, setDemoActionData, setToolCallAction, resetStateForNewTool]);

  // Check if this tool call has been executed
  useEffect(() => {
    if (toolCallId) {
      setIsExecuted(isToolCallExecuted(toolCallId));
    }
  }, [toolCallId, isToolCallExecuted, executedToolCallIds]);

  const handleRunStrategy = useCallback(async () => {
    if (!sessionId || !toolCallId || isExecuted) return;

    try {
      // Import apiClient dynamically to avoid circular dependencies
      const { apiClient } = await import("@/lib/api-client");
      
      // Send demo action to backend
      await apiClient.sendDemoAction(sessionId, toolCallId, "await_user_execution");
      
      // Mark as executed
      markToolCallExecuted(toolCallId);
      setIsExecuted(true);
      
    } catch (error) {
    }
  }, [sessionId, toolCallId, isExecuted, markToolCallExecuted]);

  // Countdown timer effect
  useEffect(() => {
    if (isExecuted) {
      setCountdown(60); // Reset countdown when executed
      return;
    }

    if (countdown <= 0) {
      handleRunStrategy(); // Auto-trigger when countdown reaches 0
      return;
    }

    const timer = setInterval(() => {
      setCountdown(prev => prev - 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [isExecuted, countdown, handleRunStrategy]);

  // Show success state if executed
  if (isExecuted) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.98 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.2, ease: "easeOut" }}
        className="w-full"
      >
        <div className={`rounded-md border ${chrome.card} overflow-hidden`}>
          <div className="px-3 py-2 flex items-center justify-between gap-3 min-w-0">
            <div className="flex items-center gap-2.5">
              <Terminal
                  className={`w-3.5 h-3.5 ${chrome.iconMuted} shrink-0`}
                  strokeWidth={2}
                />
              <div className="flex flex-col">
                <span className={`text-[13px] font-semibold ${chrome.text} truncate`}>
                  Strategy Run Triggered
                </span>
                <span className={`text-[11px] ${chrome.subtext} truncate`}>
                  Execution will continue
                </span>
              </div>
            </div>

            {/* Checkmark on the right */}
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.1, type: "spring", stiffness: 300 }}
            >
              <CircleCheckBig className="h-4 w-4 text-green-500" />
            </motion.div>
          </div>
        </div>
      </motion.div>
    );
  }

  // Show interactive button (default state)
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.98 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.2, ease: "easeOut" }}
      className="w-full"
    >
      <div className={`rounded-md border ${chrome.card} overflow-hidden`}>
        {/* Enhanced shimmer effect for clickable state */}
        <>
          {/* Outer glow with stronger pulse */}
          <div className="absolute -inset-1 bg-gradient-to-r from-zinc-400/20 via-zinc-300/30 to-zinc-400/20 rounded-lg blur-sm" 
               style={{ 
                 animation: 'shimmer-pulse 1.2s ease-in-out infinite',
               }} />
          
          {/* Fast rotating shimmer */}
          <div className="absolute -inset-0.5 rounded-lg overflow-hidden">
            <div className="absolute inset-0 bg-gradient-conic from-transparent via-zinc-400/25 to-transparent animate-spin" 
                 style={{ animationDuration: '0.8s' }} />
          </div>
        </>

        <button 
          onClick={handleRunStrategy}
          className="relative z-10 flex items-center justify-between px-3 py-2 w-full text-left hover:bg-muted/20 transition-colors"
        >
          <div className="flex items-center gap-2.5">
            <MousePointerClick
              className={`w-4 h-4 ${chrome.iconMuted}`}
              strokeWidth={2}
            />
            <div className="flex flex-col">
              <span className={`text-[13px] font-semibold ${chrome.text} truncate`}>
                Run Strategy
              </span>
              <span className={`text-[11px] ${chrome.subtext} truncate`}>
                Execute your strategy • Auto-start in {countdown}s
              </span>
            </div>
          </div>
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.1, type: "spring", stiffness: 300 }}
          >
            <Play className="h-5 w-5 text-primary" />
          </motion.div>
        </button>
      </div>
    </motion.div>
  );
}
